import { extend } from "@dhx/ts-common/core";
import { Id, IHandlers } from "@dhx/ts-common/types";
import { create, el, VNode } from "@dhx/ts-common/dom";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";

import { calculatePosition, getRealPosition, locateNode } from "@dhx/ts-common/html";
import { KeyManager, IKeyManager } from "@dhx/ts-common/KeyManager";
import { View } from "@dhx/ts-common/view";
import { TreeCollection, IDataEventsHandlersMap } from "@dhx/ts-data";
import {
	DataEvents,
	IItem,
	IGroups,
	NavigationBarEvents,
	INavbarEventHandlersMap,
	INavbarConfig,
} from "./types";

function addInGroups(groups: IGroups, item: any) {
	if (groups[item.group]) {
		if (item.active) {
			groups[item.group].active = item.id;
		}
		groups[item.group].elements.push(item.id);
	} else {
		groups[item.group] = {
			active: item.active ? item.id : null,
			elements: [item.id],
		};
	}
}

export abstract class Navbar<T extends IItem = IItem> extends View {
	public data: TreeCollection<T>;
	public events: IEventSystem<
		DataEvents | NavigationBarEvents,
		IDataEventsHandlersMap & INavbarEventHandlersMap
	>;
	public config: INavbarConfig;
	protected _vpopups: VNode;
	protected _activeMenu: Id;
	protected _activePosition: {
		left: number;
		right: number;
		top: number;
		bottom: number;
		zIndex?: number;
	};
	protected _isContextMenu = false;
	protected _handlers: IHandlers;
	protected _currentRoot: Id;
	protected _factory: (item: T, asMenuItem?: boolean) => any;
	protected _groups: IGroups;
	private _isActive: boolean;
	private _popupActive: boolean;
	private _currentTimeout: any;
	private _documentClick: (e: MouseEvent) => void;
	private _documentHaveListener = false;
	private _rootItem: any;
	private _activeParents: Id[];
	private _keyManager: IKeyManager;

	constructor(element?: string | HTMLElement, config?: any) {
		// super(element, extend({}, config));
		super(element, config);
		this.config = extend(
			{
				rootId: (typeof element === "string" && element) || this._uid,
			},
			config
		);
		this._rootItem = {};

		if (Array.isArray(this.config.data)) {
			this.events = new EventSystem<DataEvents | NavigationBarEvents>(this);
			this.data = new TreeCollection<T>({ rootId: this.config.rootId }, this.events);
		} else if (this.config.data && this.config.data.events) {
			this.data = this.config.data;
			this.events = this.data.events;
			this.events.context = this;
		} else {
			this.events = new EventSystem<DataEvents | NavigationBarEvents>(this);
			this.data = new TreeCollection<T>({ rootId: this.config.rootId }, this.events);
		}
		this._documentClick = e => {
			if (this._documentHaveListener) {
				const element = locateNode(e);
				const root = this.data.getRoot();
				const id = element && element.getAttribute("data-dhx-id");

				const parent = this.data.getParent(id);
				const supportsTouch = "ontouchstart" in window || navigator.msMaxTouchPoints;

				document.removeEventListener("mousedown", this._documentClick);
				this._documentHaveListener = false;
				if (
					(((supportsTouch && !element) || !this._isContextMenu) &&
						(root === parent || !parent || !this.data.getItem(id))) ||
					!parent ||
					!this.data.getItem(id)
				) {
					this._close(e);
				}
			}
		};
		this._currentRoot = this.data.getRoot();
		this._factory = this._getFactory();
		this._initHandlers();
		// this._init();
		this._keyManager = new KeyManager((_, focusId) => focusId === this._uid);
		this._initEvents();
		if (Array.isArray(this.config.data)) {
			this.data.parse(this.config.data);
		}
	}
	paint(): void {
		super.paint();
		if (this._isContextMenu && !this._vpopups) {
			this._init();
		}
		if (this._vpopups) {
			this._vpopups.redraw();
		}
	}
	disable(ids: Id | Id[]): void {
		if (typeof ids !== "undefined") {
			this._setProp(ids, "disabled", true);
		} else {
			this.data.forEach(({ id }) => this._setProp(id, "disabled", true));
		}
	}
	enable(ids: Id | Id[]): void {
		if (typeof ids !== "undefined") {
			this._setProp(ids, "disabled", false);
		} else {
			this.data.forEach(({ id }) => this._setProp(id, "disabled", false));
		}
	}
	isDisabled(id: Id): boolean {
		const item = this.data.getItem(id);
		if (item) {
			return item.disabled || false;
		}
	}
	show(ids: Id | Id[]): void {
		if (typeof ids !== "undefined") {
			this._setProp(ids, "hidden", false);
		} else {
			this.data.forEach(({ id }) => this._setProp(id, "hidden", false));
		}
	}
	hide(ids: Id | Id[]): void {
		if (typeof ids !== "undefined") {
			this._setProp(ids, "hidden", true);
		} else {
			this.data.forEach(({ id }) => this._setProp(id, "hidden", true));
		}
	}
	destructor(): void {
		this.events.clear();
		this._keyManager && this._keyManager.destructor();
		this._vpopups && !!this._vpopups.node && this._vpopups.unmount();
		this.unmount();
	}
	select(id: Id, unselect = true): void {
		if (!id) {
			throw new Error(
				"Function argument cannot be empty, for more info check documentation https://docs.dhtmlx.com"
			);
		}
		unselect && this.unselect();
		this.data.update(id, { active: true });
		this.data.eachParent(id, parent => {
			this.data.update(parent.id, { active: true });
		});
	}
	unselect(id?: Id): void {
		if (!id) {
			this.data.forEach(item => {
				this.data.update(item.id, { active: false });
			});
		} else {
			this.data.update(id, { active: false });
			this.data.eachChild(id, parent => {
				this.data.update(parent.id, { active: false });
			});
		}
	}
	isSelected(id: Id): boolean {
		if (!id || !this.data.getItem(id)) {
			return;
		}
		return !!this.data.getItem(id).active;
	}
	getSelected(): Id[] {
		const selected = [];
		this.data.forEach(item => {
			item.active && selected.push(item.id);
		});
		return selected;
	}
	protected abstract _getFactory(): (item: T, asMenuItem?: boolean) => any;
	protected _customHandlers() {
		return {};
	}
	protected _close(e: MouseEvent | TouchEvent): void {
		if (!this._popupActive || !this.events.fire(NavigationBarEvents.beforeHide, [this._activeMenu, e])) {
			return;
		}
		if (this._activeParents) {
			this._activeParents.forEach(
				parentId => this.data.exists(parentId) && this.data.update(parentId, { $activeParent: false })
			);
		}
		if (this.config.navigationType === "click") {
			this._isActive = false;
		}
		clearTimeout(this._currentTimeout);
		this._popupActive = false;
		this._activeMenu = null;
		!!this._vpopups.node && this._vpopups.unmount();
		this._vpopups = null;
		this.events.fire(NavigationBarEvents.afterHide, [e]);
		this.paint();
	}
	protected _init() {
		const render = () =>
			el(
				"div",
				{
					"data-dhx-widget-id": this._uid,
					class: `${this._isContextMenu ? " dhx_context-menu" : ""} ${
						this.config.css
							? this.config.css
									.split(" ")
									.map(i => (this._isContextMenu ? i : i + "--popup-menu"))
									.join(" ")
							: ""
					}`,
					onmousemove: this._handlers.onmousemove,
					onmouseleave: this._handlers.onmouseleave,
					onclick: this._handlers.onclick,
					onmousedown: this._handlers.onmousedown,
				},
				this._drawPopups()
			);
		this._vpopups = create({
			render,
		});
		this._vpopups.mount(document.body);
	}
	protected _initHandlers() {
		/*
			for navigation type click:
			first click open menu, _isActive = true
			after navigation use mousemove
			can be closed after outer click or menu leaf item click
		*/
		this._isActive = this.config.navigationType !== "click";
		this._handlers = {
			onmousemove: (e: MouseEvent) => {
				if (!this._isActive) {
					return;
				}

				const elem = locateNode(e);
				if (!elem) {
					return;
				}
				const id = elem.getAttribute("data-dhx-id");
				if (this._activeMenu !== id) {
					if (this.data.haveItems(id)) {
						if (!this._vpopups) {
							this._init();
						}
						const position = getRealPosition(elem as HTMLElement);
						this.data.update(id, { $position: position }, false);
					}
					this._activeItemChange(id, e);
				}
			},
			onmouseleave: (e: MouseEvent) => {
				if (this.config.navigationType !== "click") {
					// maybe all time when mouse leave close menu
					if (this._popupActive) {
						const element = locateNode(e, "data-dhx-id", "relatedTarget");
						if (element) {
							const id = element.getAttribute("data-dhx-id");
							if (!this.data.getItem(id)) {
								this._close(e);
								this._activeItemChange(null, e);
							}
							if (this.data.haveItems(id)) {
								if (!this._vpopups) {
									this._init();
								}
								const position = getRealPosition(element as HTMLElement);
								this.data.update(id, { $position: position }, false);
								this._activeItemChange(id, e);
							}
						} else {
							this._close(e);
							this._activeItemChange(null, e);
						}
					} else this._activeItemChange(null, e);
				}
			},
			onclick: (e: MouseEvent) => {
				const element = locateNode(e);
				if (!element) {
					return;
				}

				const id = element.getAttribute("data-dhx-id");

				if (this.isDisabled(id)) {
					return;
				}

				const item = this.data.getItem(id);
				if (item?.multiClick) {
					return;
				}

				if (this.data.haveItems(id)) {
					if (!this._vpopups) {
						this._init();
					}
					if (id === this._currentRoot) {
						return;
					}
					if (!this._isActive) {
						this._isActive = true;
					}
					this._setRoot(id);
					const position = getRealPosition(element as HTMLElement);
					this.data.update(id, { $position: position }, false);
					this._activeItemChange(id, e);
					this.events.fire(NavigationBarEvents.click, [id, e]);
				} else {
					switch (item?.type) {
						case "input":
						case "title":
							break;
						case "menuItem":
						case "selectButton":
							this._onMenuItemClick(id, e);
							break;
						case "imageButton":
						case "button":
						case "customButton":
						case "customHTML":
						case "navItem":
							if ((item as any).twoState) {
								this.data.update(item.id, { active: !item.active });
							}
							this.events.fire(NavigationBarEvents.click, [id, e]);
							this._close(e);
							break;
						default:
							this._close(e);
					}
				}
			},
			onmousedown: e => {
				const element = locateNode(e);
				if (!element) {
					return;
				}

				const id = element.getAttribute("data-dhx-id");

				const item = this.data.getItem(id);
				if (!item?.multiClick) {
					return;
				}

				let fireTime = 365;
				let timeout;

				const fireAction = () => {
					this.events.fire(NavigationBarEvents.click, [id, e]);
					if (fireTime > 50) {
						fireTime -= 55;
					}
					timeout = setTimeout(fireAction, fireTime);
				};
				const mouseup = () => {
					clearTimeout(timeout);
					document.removeEventListener("mouseup", mouseup);
				};
				fireAction();
				document.addEventListener("mouseup", mouseup);
			},
			...this._customHandlers(),
		};
	}
	protected _initEvents(): void {
		let timeout = null;
		this.data.events.on(DataEvents.change, () => {
			this.paint();

			if (timeout) {
				clearTimeout(timeout);
			}
			timeout = setTimeout(() => {
				const groups: IGroups = {};

				this.data.eachChild(
					this.data.getRoot(),
					item => {
						if (item.group) {
							(item.twoState as boolean) = true;
							addInGroups(groups, item);
						}
					},
					true
				);
				this._groups = groups;
				this._resetHotkeys();
				timeout = null;
				this.paint();
			}, 100);
		});
		this.events.on(NavigationBarEvents.click, id => {
			const item = this.data.getItem(id);
			const parent = this.data.getItem(item?.parent);

			if (parent && parent.type === "selectButton") {
				this.data.update(item.parent, { value: item.value, icon: item.icon });
			}
			if (item?.group) {
				const group = this._groups[item.group];

				if (group.active) {
					this.data.update(group.active, { active: false });
				}
				group.active = item.id;
				this.data.update(item.id, { active: true });
			}
		});
		this.events.on(NavigationBarEvents.inputChange, (id, newValue) => {
			this.data.update(id, { value: newValue });
		});
		this._customInitEvents();
	}
	protected _getMode(item: T, root: Id, _active = false): "bottom" | "right" {
		return item.parent === root ? "bottom" : "right";
	}
	protected _drawMenuItems(id: Id, asMenuItem = true) {
		return this.data.map(item => this._factory(item, asMenuItem), id, false);
	}
	protected _setRoot(_id: Id): void {
		return; // need only for toolbar
	}
	protected _getParents(id, root): Id[] {
		const parentIds = [];
		let afterRoot = false;

		const currentItem = this.data.getItem(id);
		const disabled = currentItem && currentItem.disabled;
		this.data.eachParent(
			id,
			item => {
				if (item.id === root) {
					parentIds.push(item.id);
					afterRoot = true;
				} else if (!afterRoot) {
					parentIds.push(item.id);
				}
			},
			!disabled
		);

		if (this._isContextMenu && this._activePosition) {
			parentIds.push(root);
		}
		return parentIds;
	}
	protected _listenOuterClick() {
		if (this._documentHaveListener) {
			return;
		}
		document.addEventListener("mousedown", this._documentClick, true);
		this._documentHaveListener = true;
	}
	protected _customInitEvents() {
		return;
	}
	private _drawPopups() {
		const id = this._activeMenu;
		if (!this._isContextMenu && !id) {
			return null;
		}
		const rootNode = this.getRootNode();
		const offsetParent =
			rootNode && rootNode.offsetParent && (rootNode.offsetParent as HTMLElement).offsetParent;
		let zIndex = null;
		if (
			offsetParent &&
			offsetParent.classList.contains("dhx_popup--window") &&
			offsetParent.classList.contains("dhx_popup--window_active")
		) {
			zIndex = 2147483647;
		}
		const root = this._currentRoot;
		if (this._isContextMenu && !this._activePosition) {
			return null;
		}
		const parentIds = this._getParents(id, root);
		this._activeParents = parentIds;
		parentIds.forEach(
			parentId =>
				this.data.exists(parentId) && this.data.update(parentId, { $activeParent: true }, false)
		);
		return parentIds
			.map(itemId => {
				if (!this.data.haveItems(itemId)) {
					return null;
				}
				const getAriaAttrs = item => ({
					role: "menu",
					"aria-labeledby": item.id,
					"aria-live": "polite",
				});
				const item = this.data.getItem(itemId) || this._rootItem; // for root item
				this._popupActive = true;
				return el(
					"ul",
					{
						class: "dhx_widget dhx_menu" + (this.config.menuCss ? " " + this.config.menuCss : ""),
						_key: itemId,
						_hooks: {
							didInsert: vnode => {
								const { width, height } = vnode.el.getBoundingClientRect();
								const position =
									this._isContextMenu && this._activePosition && itemId === root
										? this._activePosition
										: item.$position;
								const mode = this._getMode(item, root, position === this._activePosition);
								const style = calculatePosition(position, {
									mode: mode as any,
									auto: true,
									width,
									height,
								});
								item.$style = {
									...style,
									zIndex: this._activePosition
										? this._activePosition.zIndex || zIndex
										: zIndex,
								};
								vnode.patch({ style: item.$style });
							},
							didRecycle: (_, vnode) => {
								if (this._isContextMenu && this._activePosition && itemId === root) {
									const { width, height } = vnode.el.getBoundingClientRect();
									const style = calculatePosition(this._activePosition, {
										mode: this._getMode(item, root, true) as any,
										width,
										height,
									});
									item.$style = { ...style, zIndex: this._activePosition.zIndex || zIndex };
									vnode.patch({ style: item.$style });
								}
							},
						},
						tabindex: 0,
						style: item.$style || {
							position: "absolute",
						},
						...getAriaAttrs(item),
					},
					this._drawMenuItems(itemId)
				);
			})
			.reverse();
	}
	private _onMenuItemClick(id, e) {
		const item = this.data.getItem(id);
		if (item.disabled) {
			return;
		}
		if ((item as any).twoState) {
			this.data.update(item.id, { active: !item.active });
		}
		this.events.fire(NavigationBarEvents.click, [id, e]);
		this._close(e);
	}
	private _activeItemChange(id: Id, e: MouseEvent): void {
		if (this._activeParents) {
			const parentIds = this._getParents(id, this._currentRoot);
			this._activeParents.forEach(parentId => {
				if (this.data.exists(parentId) && !parentIds.includes(parentId)) {
					this.data.update(parentId, { $activeParent: false }, false);
				}
			});
		}
		if (id && !this._documentHaveListener) {
			this._listenOuterClick();
		}
		if (id && this.data.haveItems(id)) {
			if (this._activeMenu !== id || !this._popupActive) {
				this.events.fire(NavigationBarEvents.openMenu, [id]);
			}
			clearTimeout(this._currentTimeout);
			this.paint();
		} else {
			clearTimeout(this._currentTimeout);
			this._currentTimeout = setTimeout(() => this.paint(), 400);
		}
		this._activeMenu = id;
	}
	private _resetHotkeys(): void {
		this._keyManager.removeHotKey(null, this);
		this.data.map(item => {
			if (item.hotkey) {
				this._keyManager.addHotKey(item.hotkey, () => this._onMenuItemClick(item.id, null));
			}
		});
	}
	private _setProp(id: Id | Id[], key: string, value: any) {
		if (Array.isArray(id)) {
			id.forEach(itemId => this.data.update(itemId, { [key]: value }));
		} else {
			this.data.update(id, { [key]: value });
		}
	}
}
